import {Injectable} from '@angular/core';
import {
  AdvancedFilterGroup,
  AnalyticOperation,
  AnalyticOperationData,
  CheckboxFilterItem,
  DateRangeFilterItem,
  EventType,
  FilterGroup,
  GeneralFilter,
  GetAdvancedParams,
  GetResponse,
  InvoiceOrder,
  PaginationData,
  Partner,
  Project,
  PurchaseOrder,
  Service,
  ServiceOrder,
  ServiceType,
  ContainerAdvancedSearchState
} from '@app/shared';
import {BehaviorSubject, finalize, firstValueFrom, forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AnalyticOperationService, InvoiceOrderService, StServiceService} from '@app/core';
import {ProjectUtilsService} from '../../project/services/project-utils.service';
import {GlobalValuesService} from '../../../core/services/global-values.service';
import {AdministrationFilterService, AdministrationFilterResponse, AdministrationFilterOption} from './administration-filter.service';

type ServiceOrderSortDirection = 'asc' | 'desc';

/** Tablas de Administración con orden propio: órdenes, estados de pago, órdenes de compra y operaciones analíticas. */
export type AdministrationTable = 'so' | 'io' | 'po' | 'ao';

const SEARCH_FILTER_FIELDS: string[] = [
  'Reference',
  'ReferenceCommercial',
  'ReferenceCEC',
  'ProjectContainer.ContainerRef',
  'MBL',
  'Service.Project.Reference',
  'Service.Project.Name',
  'Service.Name',
  'Motonave.Name',
  'AnalyticOperations.Description',
  'AnalyticOperations.InvoiceOrder.Reference',
  'AnalyticOperations.PurchaseOrder.Reference'
];

// Rutas resueltas contra la ENTIDAD InvoiceOrder, no contra el DTO. Desde el EP el único camino
// al mundo operativo es la colección AnalyticOperations, así que el backend genera solo el .Any():
// io => io.AnalyticOperations.Any(x => x.ServiceOrder.ProjectContainer.ContainerRef.Contains(termino)).
// La semántica es "el EP tiene alguna OA de ese contenedor". Ver SOU-497 (CN = contenedor).
const IO_SEARCH_FILTER_FIELDS: string[] = [
  'Reference',
  'Name',
  'AnalyticOperations.Description',
  'AnalyticOperations.Details',
  'AnalyticOperations.Type',
  'AnalyticOperations.TaxType',
  'AnalyticOperations.ServiceOrder.Reference',
  'AnalyticOperations.ServiceOrder.ReferenceCEC',
  'AnalyticOperations.ServiceOrder.ReferenceCommercial',
  'AnalyticOperations.ServiceOrder.MBL',
  'AnalyticOperations.ServiceOrder.ProjectContainer.ContainerRef',
  'AnalyticOperations.ServiceOrder.Service.Name',
  'AnalyticOperations.ServiceOrder.Service.Project.Reference',
  'AnalyticOperations.ServiceOrder.Service.Project.Name',
  'AnalyticOperations.PurchaseOrder.Reference'
];

// Simétrico al de Estados de pago. Se añade 'Reference': la columna que se muestra es Reference
// ("OC-...") pero la búsqueda operaba solo sobre Name, que además es nullable, así que no se podía
// encontrar una OC por el código visible en pantalla. Ver SOU-497.
const PO_SEARCH_FILTER_FIELDS: string[] = [
  'Reference',
  'Name',
  'AnalyticOperations.Description',
  'AnalyticOperations.Details',
  'AnalyticOperations.Type',
  'AnalyticOperations.TaxType',
  'AnalyticOperations.ServiceOrder.Reference',
  'AnalyticOperations.ServiceOrder.ReferenceCEC',
  'AnalyticOperations.ServiceOrder.ReferenceCommercial',
  'AnalyticOperations.ServiceOrder.MBL',
  'AnalyticOperations.ServiceOrder.ProjectContainer.ContainerRef',
  'AnalyticOperations.ServiceOrder.Service.Name',
  'AnalyticOperations.ServiceOrder.Service.Project.Reference',
  'AnalyticOperations.ServiceOrder.Service.Project.Name',
  'AnalyticOperations.InvoiceOrder.Reference'
];

// Estas rutas las resuelve el backend por reflexión contra la ENTIDAD AnalyticOperation, que solo
// navega a ServiceOrder. Las que había aquí (Project.*, Container.*, Services.*) eran rutas del DTO
// AnalyticOperationAdminDto y NO existen en la entidad: el backend las descartaba en silencio, así
// que buscar por contenedor devolvía 0 filas y, con un chip de Tipo o Clase activo, el grupo quedaba
// reducido a esa condición base y devolvía TODAS las OA de esa clase. Ver SOU-497.
// 'Class' se retira: es un enum y un término parcial falla la conversión, que el backend sustituye
// por default(0) —valor inexistente en el enum— y no casa nunca. La clase ya tiene su propio chip.
const AO_SEARCH_FILTER_FIELDS: string[] = [
  'Description',
  'Details',
  'Type',
  'TaxType',
  'ServiceOrder.Reference',
  'ServiceOrder.ReferenceCEC',
  'ServiceOrder.ReferenceCommercial',
  'ServiceOrder.MBL',
  'ServiceOrder.ProjectContainer.ContainerRef',
  'ServiceOrder.Service.Name',
  'ServiceOrder.Service.Project.Reference',
  'ServiceOrder.Service.Project.Name',
  'InvoiceOrder.Reference',
  'InvoiceOrder.Name',
  'PurchaseOrder.Reference',
  'PurchaseOrder.Name'
];

// Punto 4 de SOU-497: la pestaña de Operaciones analíticas abre mostrando solo Ejecutado y
// Comprometido. Planificado NO se excluye —desde esta misma pantalla se promociona una OA planificada
// con change-ao-class-drawer, y excluirla dejaría inalcanzable el listado desde el que se hace ese
// trabajo—: solo se oculta por defecto, y el usuario puede volver a marcarla en el chip de Clase.
export const AO_CLASS_VALUES: string[] = ['Planned', 'Executed', 'Engaged'];
export const AO_CLASS_LABELS: Record<string, string> = {
  Planned: 'Plan',
  Executed: 'Ejecutado',
  Engaged: 'Comprometido',
};
const AO_DEFAULT_CLASS_FILTER: string[] = ['Executed', 'Engaged'];

const GENERAL_FILTER_LABELS = {
  dateRange: ' - ',
  withoutInvoice: 'Sin Estado de pago',
  withoutPurchaseOrder: 'Sin Orden de compra',
};

@Injectable({
  providedIn: 'root'
})
export class AdministrationUtilsService {

  /** Variables principales **/
  totalServiceOrders: ServiceOrder[] = [];
  filteredServiceOrders$ = new BehaviorSubject<ServiceOrder[]>([]);
  administrationView$ = new BehaviorSubject<'Ver órdenes' | 'Ver estados de pago' | 'Ver órdenes de compra' | 'Ver operaciones analíticas'>('Ver órdenes');
  syncingFromRoute = false;

  private readonly defaultSortProperty = 'Reference';
  private readonly defaultSortDirection: ServiceOrderSortDirection = 'desc';

  private currentSortProperty: string = this.defaultSortProperty;
  private currentSortDirection: ServiceOrderSortDirection = this.defaultSortDirection;
  private currentFilterGroups: FilterGroup[][] = [];
  private currentPage = 1;
  private filtersInitPromise?: Promise<void>;
  private suppressFilterTrigger = false;

  /** Variables auxiliares **/
  tableLoading$ = new BehaviorSubject<boolean>(true);

  /** Variables de logica de paginacion y filtrado **/
  paginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });

  /** Variables para InvoiceOrder (IO) **/
  filteredInvoiceOrders$ = new BehaviorSubject<InvoiceOrder[]>([]);
  ioPaginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });
  ioTableLoading$ = new BehaviorSubject<boolean>(true);
  private readonly ioDefaultSortProperty = 'Id';
  private readonly ioDefaultSortDirection: 'asc' | 'desc' = 'desc';
  private ioCurrentSortProperty: string = this.ioDefaultSortProperty;
  private ioCurrentSortDirection: 'asc' | 'desc' = this.ioDefaultSortDirection;
  private ioCurrentPage = 1;
  public ioProjectFilterValue: number = 0;

  /** Variables para PurchaseOrder (PO) **/
  filteredPurchaseOrders$ = new BehaviorSubject<PurchaseOrder[]>([]);
  poPaginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });
  poTableLoading$ = new BehaviorSubject<boolean>(true);
  private readonly poDefaultSortProperty = 'Id';
  private readonly poDefaultSortDirection: 'asc' | 'desc' = 'desc';
  private poCurrentSortProperty: string = this.poDefaultSortProperty;
  private poCurrentSortDirection: 'asc' | 'desc' = this.poDefaultSortDirection;
  private poCurrentPage = 1;
  public poProjectFilterValue: number = 0;

  /** Variables para AnalyticOperation (AO) **/
  filteredAnalyticOperations$ = new BehaviorSubject<AnalyticOperation[]>([]);
  aoPaginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });
  aoTableLoading$ = new BehaviorSubject<boolean>(true);
  private readonly aoDefaultSortProperty = 'Id';
  private readonly aoDefaultSortDirection: 'asc' | 'desc' = 'desc';
  private aoCurrentSortProperty: string = this.aoDefaultSortProperty;
  private aoCurrentSortDirection: 'asc' | 'desc' = this.aoDefaultSortDirection;
  private aoCurrentPage = 1;
  public aoSearchValue: string = '';
  public aoTypeFilterValue: string | null = null;
  // Selección múltiple: la pestaña abre con Ejecutado y Comprometido, y el chip muestra cuáles están
  // activas, que es el aviso de que hay filas ocultas. Ver SOU-497.
  public aoClassFilterValues: string[] = [...AO_DEFAULT_CLASS_FILTER];
  public aoWithoutInvoice = false;
  public aoWithoutPurchaseOrder = false;
  public aoAdvancedContainerRefs: string[] = [];
  public aoDataLoaded = false;
  public aoNeedsReload = true;
  private aoLastState: {
    typeFilter: string | null;
    classFilter: string;
    withoutInvoice: boolean;
    withoutPurchaseOrder: boolean;
    containerRefs: string;
    searchValue: string;
    page: number;
    pageSize: number
  } | null = null;

  filters: AdvancedFilterGroup[] = [];

  activeFilters$ = new BehaviorSubject<number>(0);

  // Search values separados para IO y PO
  ioSearchValue: string = '';
  poSearchValue: string = '';

  // Flags para trackear si ya se cargaron los datos
  public ioDataLoaded = false;
  public poDataLoaded = false;

  // Estado global para controlar si se deben hacer peticiones al cambiar de sección
  public ioNeedsReload = true; // Inicialmente necesita carga
  public poNeedsReload = true; // Inicialmente necesita carga
  private ioLastState: { projectFilter: number; pendingFilter: boolean; page: number; pageSize: number } | null = null;
  private poLastState: { projectFilter: number; pendingFilter: boolean; page: number; pageSize: number } | null = null;

  generalFilters: GeneralFilter[] = [
    {
      name: '15 MAR - 27 MAR',
      icon: 'calendar_today',
      active: false,
      value: {startDate: '2023-03-15', endDate: '2023-03-27'},
    },
    {
      name: 'Sin Estado de pago',
      icon: 'receipt_long_off',
      active: false,
    },
    {
      name: 'Sin Orden de compra',
      icon: 'shopping_cart_off',
      active: false,
    },
    {
      name: 'Pendientes de facturar',
      icon: 'pending_actions',
      active: false,
    },
    {
      name: 'Proyecto',
      icon: 'business',
      active: false,
      type: 'select',
      options: [],
    },
  ];
  searchValue: string = '';
  soSearchTerms: string[] = [];
  soParallelSearchMode = false;
  advancedContainerRefs: string[] = [];
  advancedContainerCombineWithCurrentFilters = false;
  advancedSearchDrawerState: ContainerAdvancedSearchState = this.createInitialAdvancedSearchState();

  constructor(
    private stServiceService: StServiceService,
    private invoiceOrderService: InvoiceOrderService,
    private analyticOperationService: AnalyticOperationService,
    private ps: ProjectUtilsService,
    private gs: GlobalValuesService,
    private administrationFilterService: AdministrationFilterService,
  ) {
    this.resetFilter();
  }

  private async ensureFiltersInitialized(): Promise<void> {
    if (!this.filtersInitPromise) {
      this.filtersInitPromise = this.loadAdvancedFilters();
    }
    return this.filtersInitPromise;
  }

  private async loadAdvancedFilters(): Promise<void> {
    try {
      const responses = await firstValueFrom(
        forkJoin({
          states: this.administrationFilterService.getStates().pipe(catchError(() => of({data: []}))),
          projects: this.administrationFilterService.getProjects().pipe(catchError(() => of({data: []}))),
          services: this.administrationFilterService.getServices().pipe(catchError(() => of({data: []}))),
          serviceTypes: this.administrationFilterService.getServiceTypes().pipe(catchError(() => of({data: []}))),
          mbls: this.administrationFilterService.getMbls().pipe(catchError(() => of({data: []}))),
          containerRefs: this.administrationFilterService.getContainerReferences().pipe(catchError(() => of({data: []}))),
          cecRefs: this.administrationFilterService.getCecReferences().pipe(catchError(() => of({data: []}))),
          suppliers: this.administrationFilterService.getSuppliers().pipe(catchError(() => of({data: []}))),
          clients: this.administrationFilterService.getClients().pipe(catchError(() => of({data: []}))),
          analyticOps: this.administrationFilterService.getAnalyticOperations().pipe(catchError(() => of({data: []}))),
          invoiceOrders: this.administrationFilterService.getInvoiceOrders().pipe(catchError(() => of({data: []}))),
          purchaseOrders: this.administrationFilterService.getPurchaseOrders().pipe(catchError(() => of({data: []}))),
        })
      );

      this.resetFilter();

      this.populateStateFilter(responses.states.data);
      this.populateCheckboxFilter('Proyecto', responses.projects.data, 'Service.Project.Id', (item) => item.name || item.reference || '-');
      this.populateCheckboxFilter('Servicio', responses.services.data, 'Service.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('Tipo de servicio', responses.serviceTypes.data, 'Service.Type.Id', (item) => item.name || '-');
      this.populateCheckboxFilter('MBL', responses.mbls.data, 'MBL', (item) => (item.value || '').toString());
      this.populateCheckboxFilter('Referencia de contenedor', responses.containerRefs.data, 'ProjectContainer.ContainerRef', (item) => (item.value || '').toString());
      this.populateCheckboxFilter('Referencia CEC', (responses.cecRefs.data || []).sort((a, b) => ((a.value || '') as string).localeCompare((b.value || '') as string, 'es')), 'ReferenceCEC', (item) => (item.value || '').toString());
      // Cambiar a SupplierIds para soportar múltiples suppliers
      // El backend debe soportar búsqueda en arrays con este path
      this.populateCheckboxFilter('Proveedor', responses.suppliers.data, 'Service.SupplierIds', (item) => item.name || '-');
      this.populateCheckboxFilter('Cliente', responses.clients.data, 'Service.Project.Partner.Id', (item) => item.name || '-');
      // El listado de OA llega con una entrada por registro y muchas comparten descripción, así que
      // se filtra por descripción (no por Id) para que marcar un nombre devuelva TODAS las OA que se
      // llaman así; la guarda de `seen` colapsa entonces las repeticiones. Ver SOU-496.
      this.populateCheckboxFilter(
        'Operaciones analíticas',
        (responses.analyticOps.data || []).sort((a, b) => (a.description ?? '').localeCompare(b.description ?? '', 'es')),
        'AnalyticOperations.Description',
        (item) => item.description?.trim() || '-',
        (item) => item.description?.trim() || null,
      );
      this.populateCheckboxFilter('Estados de pago', responses.invoiceOrders.data, 'AnalyticOperations.InvoiceOrderId', (item) => item.reference || '-');
      this.populateCheckboxFilter('Órdenes de compra', responses.purchaseOrders.data, 'AnalyticOperations.PurchaseOrderId', (item) => item.reference || '-');
      this.populateDateRangeFilter('Fecha tentativa de entrega', 'TentativeDeliveryDate');

      this.filters?.forEach(fg => {
        fg.filtersActives = 0;
        fg.shownFiltersItems = [...fg.advancedFiltersItems];
      });

      this.mergeFilters(false);

      if (!this.storageFilters.length) {
        this.suppressFilterTrigger = true;
        this.storageFilters = this.cloneFilters(this.filters);
        this.suppressFilterTrigger = false;
      }

      this.setActiveFilters();
    } catch (error) {
      console.error('Error al cargar filtros de administración:', error);
    }
  }

  private populateStateFilter(data: AdministrationFilterOption[]): void {
    const filter = this.findFilterByName('Estado');
    if (!filter) {
      return;
    }

    const groupedStates = new Map<string, number[]>();
    data?.forEach(item => {
      const name = item.name || '-';
      const rawValue = item.value ?? item.id;
      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        return;
      }
      const list = groupedStates.get(name) ?? [];
      if (!list.includes(numericValue)) {
        list.push(numericValue);
      }
      groupedStates.set(name, list);
    });

    filter.advancedFiltersItems = [];
    groupedStates?.forEach((ids, name) => {
      filter.advancedFiltersItems.push({
        label: name,
        value: {name, sequence: 0},
        idGroup: ids,
        isActive: false,
        type: 'event'
      });
    });

    filter.filtersActives = 0;
    filter.shownFiltersItems = [...filter.advancedFiltersItems];
  }

  private populateCheckboxFilter(
    filterName: string,
    data: AdministrationFilterOption[],
    path: string,
    labelFactory: (option: AdministrationFilterOption) => string,
    valueFactory?: (option: AdministrationFilterOption) => number | string | null | undefined,
  ): void {
    const filter = this.findFilterByName(filterName);
    if (!filter) {
      return;
    }

    const seen = new Set<string | number>();
    filter.advancedFiltersItems = [];

    data?.forEach(item => {
      const value = valueFactory ? valueFactory(item) : (item.id ?? item.value);
      if (value === undefined || value === null) {
        return;
      }

      const uniqueKey = `${path}-${value}`;
      if (seen.has(uniqueKey)) {
        return;
      }
      seen.add(uniqueKey);

      const label = labelFactory(item) || '-';
      filter.advancedFiltersItems.push({
        label,
        value,
        path,
        type: 'checkbox',
        isActive: false,
      });
    });

    filter.filtersActives = 0;
    filter.shownFiltersItems = [...filter.advancedFiltersItems];
  }

  private findFilterByName(name: string): AdvancedFilterGroup | undefined {
    return this.filters.find(f => f.name === name);
  }

  private populateDateRangeFilter(filterName: string, path: string): void {
    const filter = this.findFilterByName(filterName);
    if (!filter) {
      return;
    }

    filter.advancedFiltersItems = [{
      label: 'Fecha tentativa de entrega',
      isActive: true,
      type: 'date-range',
      value: {useRange: false, exactDate: '', startDate: '', endDate: ''},
      path,
    } as DateRangeFilterItem];

    filter.filtersActives = 0;
    filter.shownFiltersItems = [...filter.advancedFiltersItems];
  }

  private cloneFilters(filters: AdvancedFilterGroup[]): AdvancedFilterGroup[] {
    return JSON.parse(JSON.stringify(filters));
  }

  // operatorValue: 0 = Equals, 1 = Contains. Se puede forzar con el cuarto argumento; si no se pasa,
  // se mantiene la lista de propiedades que ya exigían igualdad estricta.
  private createEqualsCondition(
    property: string,
    value: any,
    collectionGroup?: string,
    operatorValue?: number,
  ): FilterGroup {
    const condition: FilterGroup = {
      property,
      value: this.toFilterValue(value),
      operator: 'Equals',
      // operatorValue 0 = Equals, 1 = Contains. Description es string, así que sin el 0 el backend
      // emparejaría por subcadena y "TRANSPORTE CNTR" arrastraría "TRANSPORTE CNTR REFRIGERADO".
      operatorValue: operatorValue !== undefined
        ? operatorValue
        : ((property === 'AnalyticOperations.InvoiceOrderId'
          || property === 'AnalyticOperations.PurchaseOrderId'
          || property === 'AnalyticOperations.Description') ? 0 : 1)
    };

    if (collectionGroup) {
      condition.collectionGroup = collectionGroup;
    }

    return condition;
  }

  private toFilterValue(value: any): string | number | boolean | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return String(value);
  }

  private getMinuteRangeBounds(value: any): { start: string; end: string } | null {
    const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const start = new Date(date.getTime());
    start.setSeconds(0, 0);

    const end = new Date(start.getTime());
    end.setMinutes(end.getMinutes() + 1);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  }

  private cloneCondition(condition: FilterGroup): FilterGroup {
    return {
      property: condition.property,
      value: condition.value,
      operator: condition.operator,
      operatorValue: condition.operatorValue,
      collectionGroup: condition.collectionGroup,
    };
  }

  _storageFilters: AdvancedFilterGroup[] | undefined;

  get storageFilters(): AdvancedFilterGroup[] {
    // Siempre leer directamente de localStorage para consistencia
    const stored = localStorage.getItem('advancedFilters');
    const parsed = stored ? JSON.parse(stored) : [];

    // Sincronizar con la propiedad interna
    if (!this._storageFilters || JSON.stringify(this._storageFilters) !== stored) {
      this._storageFilters = parsed;
    }

    return this._storageFilters || [];
  }

  set storageFilters(value: AdvancedFilterGroup[]) {
    const stringified = JSON.stringify(value);
    localStorage.setItem('advancedFilters', stringified);
    this._storageFilters = value;
    this.setActiveFilters();
    if (!this.suppressFilterTrigger) {
      this.filter();
    }
  }

  async getServiceOrders(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
  } = {}): Promise<void> {
    const {resetPage, page, skipPageAdjustment} = options;

    if (resetPage) {
      this.currentPage = 1;
    }

    if (page !== undefined) {
      this.currentPage = Math.max(1, page);
    }

    const paginationSnapshot = this.paginationData$.value;
    const pageSize = paginationSnapshot.pageSize || 10;
    const startRow = (this.currentPage - 1) * pageSize;
    const endRow = startRow + pageSize;

    await this.ensureFiltersInitialized();

    const filterGroups = this.buildFilterGroups();
    this.currentFilterGroups = filterGroups;

    const params: GetAdvancedParams = {
      startRow,
      endRow,
      sortProperty: this.currentSortProperty,
      sortDirection: this.currentSortDirection,
      filterGroups: filterGroups.length > 0 ? filterGroups : undefined,
    };

    this.tableLoading$.next(true);

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.stServiceService.getAdvancedServiceOrderForAdmin(params)
          .pipe(finalize(() => this.tableLoading$.next(false)))
      );

      const rows = (rsp.data?.rows || []).filter(so => !!so.project && !!so.service);
      rows?.forEach(so => this.setOrderAndMoneyData(so));

      const totalItems = rsp.data?.lastRow ?? rows.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.currentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.currentPage = targetPage;
        await this.getServiceOrders({
          page: targetPage,
          skipPageAdjustment: true
        });
        return;
      }

      this.totalServiceOrders = rows;
      this.filteredServiceOrders$.next(rows);

      if (effectiveTotalPages > 0 && this.currentPage > effectiveTotalPages) {
        this.currentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.currentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.currentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.paginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.currentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.setActiveFilters();
    } catch (e) {
      console.error('Error al obtener ServiceOrders:', e);
      this.tableLoading$.next(false);
    }
  }

  updateLocalData(_: ServiceOrder[]): void {
    this.getServiceOrders({page: this.currentPage});
  }

  setPageSize(pageSize: number, reload: boolean): void {
    const sanitizedPageSize = Math.max(1, pageSize);
    const paginationSnapshot = this.paginationData$.value;

    if (paginationSnapshot.pageSize === sanitizedPageSize) {
      return;
    }

    this.paginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    this.currentPage = 1;

    if (reload) {
      this.getServiceOrders({resetPage: true});
    }
  }

  setCurrentPage(page: number): void {
    this.currentPage = Math.max(1, page);
  }

  getCurrentPageForSection(view: 'Ver órdenes' | 'Ver estados de pago' | 'Ver órdenes de compra' | 'Ver operaciones analíticas'): number {
    switch (view) {
      case 'Ver órdenes':
        return this.currentPage;
      case 'Ver estados de pago':
        return this.ioCurrentPage;
      case 'Ver órdenes de compra':
        return this.poCurrentPage;
      case 'Ver operaciones analíticas':
        return this.aoCurrentPage;
      default:
        return 1;
    }
  }

  async updateSort(sortProperty: string, sortDirection: ServiceOrderSortDirection): Promise<void> {
    this.currentSortProperty = sortProperty;
    this.currentSortDirection = sortDirection;
    await this.getServiceOrders({resetPage: true});
  }

  async resetSort(): Promise<void> {
    this.currentSortProperty = this.defaultSortProperty;
    this.currentSortDirection = this.defaultSortDirection;
    await this.getServiceOrders({resetPage: true});
  }

  /**
   * Orden vigente de cada tabla. El servicio es global y lo conserva al salir de
   * Administración; el componente se recrea, así que lo lee de aquí para pintar las cabeceras.
   */
  getCurrentSort(table: AdministrationTable): {property: string, direction: 'asc' | 'desc'} {
    switch (table) {
      case 'io':
        return {property: this.ioCurrentSortProperty, direction: this.ioCurrentSortDirection};
      case 'po':
        return {property: this.poCurrentSortProperty, direction: this.poCurrentSortDirection};
      case 'ao':
        return {property: this.aoCurrentSortProperty, direction: this.aoCurrentSortDirection};
      default:
        return {property: this.currentSortProperty, direction: this.currentSortDirection};
    }
  }

  private buildFilterGroups(): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const andConditions: FilterGroup[] = [];
    const orAlternatives: FilterGroup[][][] = [];

    const dateFilter = this.generalFilters[0];
    const dateFilterHasRange = dateFilter?.active
      && dateFilter.value?.startDate
      && dateFilter.value?.endDate;

    if (dateFilterHasRange) {
      baseConditions.push(
        {
          property: 'CreatedAt',
          value: dateFilter.value.endDate,
          operator: 'LessThanOrEqual'
        },
        {
          property: 'CreatedAt',
          value: dateFilter.value.startDate,
          operator: 'GreaterThanOrEqual'
        },
      );
    }

    const withoutInvoiceFilter = this.generalFilters.find(
      f => f.name === GENERAL_FILTER_LABELS.withoutInvoice
    );
    if (withoutInvoiceFilter?.active) {
      andConditions.push(
        this.createEqualsCondition('AnalyticOperations.InvoiceOrderId', null, 'aoWithoutInvoice:all'),
      );
    }

    const withoutPurchaseOrderFilter = this.generalFilters.find(
      f => f.name === GENERAL_FILTER_LABELS.withoutPurchaseOrder
    );
    if (withoutPurchaseOrderFilter?.active) {
      andConditions.push(
        this.createEqualsCondition('AnalyticOperations.PurchaseOrderId', null, 'aoWithoutPurchaseOrder:all'),
      );
    }

    const shouldApplyTextSearch = this.advancedContainerRefs.length === 0 || this.advancedContainerCombineWithCurrentFilters;
    if (shouldApplyTextSearch && this.soParallelSearchMode && this.soSearchTerms?.length > 0) {
      // OR entre términos: un grupo por cada (campo, término) para devolver filas que coincidan con CUALQUIERA de los términos.
      const searchOptions: FilterGroup[][] = [];
      this.soSearchTerms.forEach(term => {
        const trimmed = term.trim();
        if (trimmed) {
          SEARCH_FILTER_FIELDS.forEach(property => {
            searchOptions.push([{
              property,
              value: trimmed,
              operator: 'Contains',
              operatorValue: 1
            } as FilterGroup]);
          });
        }
      });
      if (searchOptions.length > 0) {
        orAlternatives.push(searchOptions);
      }
    } else if (shouldApplyTextSearch) {
      const searchValue = (this.searchValue || '').trim();
      if (searchValue) {
        const searchAlternatives = SEARCH_FILTER_FIELDS.map(property => ([
          {
            property,
            value: searchValue,
            operator: 'Contains',
            operatorValue: 1
          } as FilterGroup
        ]));
        if (searchAlternatives.length) {
          orAlternatives.push(searchAlternatives);
        }
      }
    }

    const containerRefOrValues = new Set<string>(
      this.advancedContainerRefs.map(ref => ref.trim().toUpperCase()).filter(Boolean)
    );

    this.filters
      .filter(fg => fg.filtersActives > 0)
      ?.forEach(fg => {
        const activeItems = fg.advancedFiltersItems.filter(item => item.isActive);
        if (!activeItems.length) {
          return;
        }

        switch (activeItems[0].type) {
          case 'checkbox': {
            const checkboxItems = activeItems as CheckboxFilterItem[];
            const isContainerRefFilter = checkboxItems.every(item => item.path === 'ProjectContainer.ContainerRef');
            if (isContainerRefFilter) {
              checkboxItems.forEach(item => {
                const normalized = String(item.value).trim().toUpperCase();
                if (normalized) containerRefOrValues.add(normalized);
              });
              break;
            }

            if (checkboxItems.length === 1) {
              const item = checkboxItems[0];
              andConditions.push(this.createEqualsCondition(item.path, item.value));
            } else {
              const alternatives = checkboxItems.map(item => [
                this.createEqualsCondition(item.path, item.value)
              ]);
              if (alternatives.length) {
                orAlternatives.push(alternatives);
              }
            }
            break;
          }
          case 'event': {
            const alternatives: FilterGroup[][] = [];
            activeItems?.forEach(item => {
              const ids = (item as any).idGroup as number[] | undefined;
              if (ids && ids.length) {
                ids?.forEach(id => {
                  alternatives.push([this.createEqualsCondition('State', id)]);
                });
              }
            });

            if (alternatives.length === 1) {
              andConditions.push(...alternatives[0]);
            } else if (alternatives.length > 1) {
              orAlternatives.push(alternatives);
            }
            break;
          }
          case 'date-range': {
            const dateRangeItem = activeItems[0] as DateRangeFilterItem;
            if (dateRangeItem.value?.useRange) {
              if (dateRangeItem.value?.startDate && dateRangeItem.value?.endDate) {
                andConditions.push(
                  {property: dateRangeItem.path, value: dateRangeItem.value.endDate, operator: 'LessThanOrEqual'},
                  {property: dateRangeItem.path, value: dateRangeItem.value.startDate, operator: 'GreaterThanOrEqual'}
                );
              }
            } else if (dateRangeItem.value?.exactDate) {
              const bounds = this.getMinuteRangeBounds(dateRangeItem.value.exactDate);
              if (bounds) {
                andConditions.push(
                  {property: dateRangeItem.path, value: bounds.start, operator: 'GreaterThanOrEqual'},
                  {property: dateRangeItem.path, value: bounds.end, operator: 'LessThan'}
                );
              }
            }
            break;
          }
          default:
            // Ignorar sliders u otros tipos por ahora
            break;
        }
      });

    if (containerRefOrValues.size > 0) {
      orAlternatives.push(
        [...containerRefOrValues].map(containerRef => [
          this.createEqualsCondition('ProjectContainer.ContainerRef', containerRef)
        ])
      );
    }

    const baseAndConditions = [...baseConditions, ...andConditions];
    const filterGroups: FilterGroup[][] = [];

    if (orAlternatives.length === 0) {
      if (baseAndConditions.length === 0) {
        return [];
      }
      filterGroups.push(baseAndConditions.map(condition => this.cloneCondition(condition)));
      return filterGroups;
    }

    let combinations: FilterGroup[][] = [[]];
    orAlternatives?.forEach(alternatives => {
      const nextCombinations: FilterGroup[][] = [];
      combinations?.forEach(existing => {
        alternatives?.forEach(option => {
          nextCombinations.push([
            ...existing.map(condition => this.cloneCondition(condition)),
            ...option.map(condition => this.cloneCondition(condition)),
          ]);
        });
      });
      combinations = nextCombinations;
    });

    combinations?.forEach(combo => {
      const combined = [
        ...combo,
        ...baseAndConditions.map(condition => this.cloneCondition(condition))
      ];
      filterGroups.push(combined);
    });

    return filterGroups;
  }

  private createInitialAdvancedSearchState(): ContainerAdvancedSearchState {
    return {
      searchInput: '',
      rows: [],
      summary: {
        total: 0,
        found: 0,
        notFound: 0,
        invalid: 0,
      },
      searched: false,
    };
  }

  async fetchServiceOrderByReference(reference: string): Promise<ServiceOrder | null> {
    if (!reference) {
      return null;
    }

    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 1,
      sortProperty: this.defaultSortProperty,
      sortDirection: this.defaultSortDirection,
      filterGroups: [
        [
          {
            property: 'Reference',
            value: reference,
            operator: 'Equals'
          }
        ]
      ],
    };

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.stServiceService.getAdvancedServiceOrderForAdmin(params)
      );

      const row = rsp.data?.rows?.[0];
      if (!row) {
        return null;
      }

      this.setOrderAndMoneyData(row);
      return row;
    } catch (error) {
      console.error('Error al obtener ServiceOrder por referencia:', error);
      return null;
    }
  }

  async fetchServiceOrdersByContainerRefs(containerRefs: string[]): Promise<ServiceOrder[]> {
    const normalizedRefs = [...new Set(
      (containerRefs || [])
        .map(ref => (ref || '').trim().toUpperCase())
        .filter(Boolean)
    )];

    if (normalizedRefs.length === 0) {
      return [];
    }

    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: Math.max(2000, normalizedRefs.length * 5),
      sortProperty: this.defaultSortProperty,
      sortDirection: this.defaultSortDirection,
      filterGroups: normalizedRefs.map(containerRef => ([
        {
          property: 'ProjectContainer.ContainerRef',
          value: containerRef,
          operator: 'Equals'
        }
      ])),
    };

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.stServiceService.getAdvancedServiceOrderForAdmin(params)
      );
      return (rsp.data?.rows || []) as ServiceOrder[];
    } catch (error) {
      console.error('Error al obtener ServiceOrders por contenedor:', error);
      return [];
    }
  }

  async fetchInvoiceOrderByReference(reference: string): Promise<InvoiceOrder | null> {
    if (!reference) {
      return null;
    }

    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 1,
      sortProperty: this.ioDefaultSortProperty,
      sortDirection: this.ioDefaultSortDirection,
      filterGroups: [
        [
          {
            property: 'Reference',
            value: reference,
            operator: 'Equals'
          }
        ]
      ],
    };

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.invoiceOrderService.getAdvancedInvoiceOrderForAdmin(params)
      );

      const row = rsp.data?.rows?.[0];
      return row ? (row as InvoiceOrder) : null;
    } catch (error) {
      console.error('Error al obtener InvoiceOrder por referencia:', error);
      return null;
    }
  }

  async fetchPurchaseOrderByName(name: string): Promise<PurchaseOrder | null> {
    if (!name) {
      return null;
    }

    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: 1,
      sortProperty: this.poDefaultSortProperty,
      sortDirection: this.poDefaultSortDirection,
      filterGroups: [
        [
          {
            property: 'Name',
            value: name,
            operator: 'Equals'
          }
        ]
      ],
    };

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.invoiceOrderService.getAdvancedPurchaseOrderForAdmin(params)
      );

      const row = rsp.data?.rows?.[0];
      return row ? (row as PurchaseOrder) : null;
    } catch (error) {
      console.error('Error al obtener PurchaseOrder por nombre/referencia:', error);
      return null;
    }
  }

  setOrderAndMoneyData(serviceOrder: ServiceOrder) {
    serviceOrder.hasIngreso=false
    serviceOrder.hasGasto=false

    // Eliminar AOs duplicadas por ID (el endpoint admin las devuelve duplicadas).
    // Se conserva la copia más completa (la que tenga invoiceOrder/purchaseOrder anidados).
    if (serviceOrder.analyticOperations?.length) {
      const bestByid = new Map<number, AnalyticOperation>();
      for (const ao of serviceOrder.analyticOperations) {
        if (ao.id == null) continue;
        const existing = bestByid.get(ao.id);
        if (!existing) {
          bestByid.set(ao.id, ao);
        } else {
          // Conservar la copia que tenga invoiceOrder o purchaseOrder poblados
          if (ao.invoiceOrder && !existing.invoiceOrder) {
            existing.invoiceOrder = ao.invoiceOrder;
          }
          if (ao.purchaseOrder && !existing.purchaseOrder) {
            existing.purchaseOrder = ao.purchaseOrder;
          }
        }
      }
      serviceOrder.analyticOperations = [...bestByid.values()];
    }

    serviceOrder.analyticOperations?.forEach(ao => {
      if (ao.class === 'Engaged') {
        if (ao.type === 'Ingreso' && !ao.invoiceOrderId) {
          serviceOrder.hasIngreso = true;
        }

        if (ao.type === 'Gasto' && !ao.purchaseOrderId) {
          serviceOrder.hasGasto = true;
        }
      }

      ao.currency = this.gs.currencies$.value?.find(c => c.id === ao.currencyId);
    });

    serviceOrder.moneyData = this.ps.processServiceOrderForMoneyData(serviceOrder);
  }

  resetFilter() {
    this.filters = [
      {
        name: 'Estado',
        advancedFiltersItems: [], // Estados de los Services involucrados *** Hacer logica especial***
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Proyecto',
        advancedFiltersItems: [], // Proyectos involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Servicio',
        advancedFiltersItems: [], // Servicios involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Tipo de servicio',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'MBL',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Referencia de contenedor',
        advancedFiltersItems: [], // Tipos de servicio de los Services involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Referencia CEC',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Proveedor',
        advancedFiltersItems: [], // Partners involucrados como Proveedores (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Cliente',
        advancedFiltersItems: [], // Partners involucrados como Clientes (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Operaciones analíticas',
        advancedFiltersItems: [], // Operaciones analíticas involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Estados de pago',
        advancedFiltersItems: [], // Estados de pago involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Órdenes de compra',
        advancedFiltersItems: [], // Estados de pago involucrados (id y name)
        shownFiltersItems: [],
        filtersActives: 0,
      },
      {
        name: 'Fecha tentativa de entrega',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0,
      },
    ];
  }

  // Se unen los filtros guardados con las opciones actualizadas
  mergeFilters(updateStorage: boolean = true) {
    // Se recorre la lista de FilterGroups con las opciones actualizadas
    this.filters?.forEach(fg => {

      // Se revisa si este FilterGroup ya existia y si tiene algun filtro activo
      const currentMatchFG = this.storageFilters
        .find(storageFilterGroup => storageFilterGroup.name === fg.name && storageFilterGroup.filtersActives > 0);

      // Si existe un FilterGroup con algun filtro activo se recorre la lista de FilterItems de las opciones actualizadas
      // junto con la lista de FilterItems de los filtros guardados
      if (currentMatchFG) {
        fg.advancedFiltersItems?.forEach(fi => {

          // Se revisa si el FilterItem ya existia en las opciones guardadas
          const currentMatchFI = currentMatchFG.advancedFiltersItems
            .find(storageFilterItem => storageFilterItem.label === fi.label && (storageFilterItem.type === 'checkbox' ? (storageFilterItem.value === fi.value) : true));

          // Si existia se actualizan los valores y el estado de activo
          if (currentMatchFI && currentMatchFI.isActive) {
            fi.isActive = true;

            fg.filtersActives++;

            if (fi.type === 'slider' && currentMatchFI.type === 'slider') {
              const minValue = Math.max(currentMatchFI.value[0], fi.limits.min);
              const maxValue = Math.min(currentMatchFI.value[1], fi.limits.max);
              fi.value = [Math.min(minValue, maxValue), Math.max(maxValue, minValue)];
            } else if (fi.type === 'date-range' && currentMatchFI.type === 'date-range') {
              fi.value = {...currentMatchFI.value};
            }
          }
        })
      }
    });

    if (updateStorage && !this.storageFilters.length) this.storageFilters = [...this.filters];
  }

  setActiveFilters() {
    const advancedFiltersCount = this.filters.reduce((total, group) => {
      return total + (group.filtersActives || 0);
    }, 0);

    const generalFiltersCount = this.generalFilters.reduce((total, filter, index) => {
      if (!filter.active) {
        return total;
      }

      if (index === 0 && filter.name.includes(GENERAL_FILTER_LABELS.dateRange)) {
        return total + 1;
      }

      if (
        filter.name === GENERAL_FILTER_LABELS.withoutInvoice
        || filter.name === GENERAL_FILTER_LABELS.withoutPurchaseOrder
      ) {
        return total + 1;
      }

      return total;
    }, 0);

    this.activeFilters$.next(advancedFiltersCount + generalFiltersCount);
  }

  filter(resetPage: boolean = true): Promise<void> {
    return this.getServiceOrders({resetPage});
  }

  // ========== MÉTODOS PARA INVOICE ORDER (IO) ==========

  private buildIOFilterGroups(): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const andConditions: FilterGroup[] = [];
    const orAlternatives: FilterGroup[][][] = [];

    // Filtro de búsqueda de texto usando ioSearchValue
    const searchValue = (this.ioSearchValue || '').trim();
    if (searchValue) {
      const searchAlternatives = IO_SEARCH_FILTER_FIELDS.map(property => ([
        {
          property,
          value: searchValue,
          operator: 'Contains',
          operatorValue: 1
        } as FilterGroup
      ]));
      if (searchAlternatives.length) {
        orAlternatives.push(searchAlternatives);
      }
    }

    // Nota: El filtro de Proyecto se maneja con filterProperty y filterValue, no con filterGroups

    const baseAndConditions = [...baseConditions, ...andConditions];
    const filterGroups: FilterGroup[][] = [];

    if (orAlternatives.length === 0) {
      if (baseAndConditions.length === 0) {
        return [];
      }
      filterGroups.push(baseAndConditions.map(condition => this.cloneCondition(condition)));
      return filterGroups;
    }

    let combinations: FilterGroup[][] = [[]];
    orAlternatives?.forEach(alternatives => {
      const nextCombinations: FilterGroup[][] = [];
      combinations?.forEach(existing => {
        alternatives?.forEach(option => {
          nextCombinations.push([
            ...existing.map(condition => this.cloneCondition(condition)),
            ...option.map(condition => this.cloneCondition(condition)),
          ]);
        });
      });
      combinations = nextCombinations;
    });

    combinations?.forEach(combo => {
      const combined = [
        ...combo,
        ...baseAndConditions.map(condition => this.cloneCondition(condition))
      ];
      filterGroups.push(combined);
    });

    return filterGroups;
  }

  // Método para verificar si IO necesita recarga basado en el estado actual
  checkIONeedsReload(pendingInvoiceFilter: boolean, projectFilterValue: number, page: number, pageSize: number): boolean {
    const currentState = {
      projectFilter: projectFilterValue,
      pendingFilter: pendingInvoiceFilter,
      page: page,
      pageSize: pageSize
    };

    // Si no hay estado guardado, necesita recarga
    if (!this.ioLastState) {
      return true;
    }

    // Si el estado cambió, necesita recarga
    const needsReload =
      this.ioLastState.projectFilter !== currentState.projectFilter ||
      this.ioLastState.pendingFilter !== currentState.pendingFilter ||
      this.ioLastState.page !== currentState.page ||
      this.ioLastState.pageSize !== currentState.pageSize;

    return needsReload || this.ioNeedsReload;
  }

  // Método para verificar si PO necesita recarga basado en el estado actual
  checkPONeedsReload(pendingInvoiceFilter: boolean, projectFilterValue: number, page: number, pageSize: number): boolean {
    const currentState = {
      projectFilter: projectFilterValue,
      pendingFilter: pendingInvoiceFilter,
      page: page,
      pageSize: pageSize
    };

    // Si no hay estado guardado, necesita recarga
    if (!this.poLastState) {
      return true;
    }

    // Si el estado cambió, necesita recarga
    const needsReload =
      this.poLastState.projectFilter !== currentState.projectFilter ||
      this.poLastState.pendingFilter !== currentState.pendingFilter ||
      this.poLastState.page !== currentState.page ||
      this.poLastState.pageSize !== currentState.pageSize;

    return needsReload || this.poNeedsReload;
  }

  async getInvoiceOrders(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
    pendingInvoiceFilter?: boolean;
    forceReload?: boolean;
  } = {}): Promise<void> {
    const {resetPage, page, skipPageAdjustment, pendingInvoiceFilter, forceReload} = options;

    if (resetPage) {
      this.ioCurrentPage = 1;
    }

    if (page !== undefined) {
      this.ioCurrentPage = Math.max(1, page);
    }

    const paginationSnapshot = this.ioPaginationData$.value;
    const pageSize = paginationSnapshot.pageSize && paginationSnapshot.pageSize > 0 ? paginationSnapshot.pageSize : 10;
    const currentPage = this.ioCurrentPage;

    // Verificar si necesita recarga usando el estado global (usar el pageSize del estado guardado, no el calculado)
    const needsReload = this.checkIONeedsReload(pendingInvoiceFilter || false, this.ioProjectFilterValue, currentPage, paginationSnapshot.pageSize || 10);

    // Si no es una recarga forzada y no necesita recarga según el estado, no hacer petición
    if (!forceReload && !needsReload && this.ioDataLoaded && !resetPage && page === undefined) {
      return;
    }
    const startRow = (this.ioCurrentPage - 1) * pageSize;
    const endRow = startRow + pageSize;

    const filterGroups = this.buildIOFilterGroups();

    // Agregar filtro de "Pendientes de facturar" si está activo - para IO: solo ClientInvoiceId
    if (pendingInvoiceFilter) {
      const pendingConditions: FilterGroup[] = [
        {
          property: 'ClientInvoiceId',
          value: null,
          operator: 'Equals'
        }
      ];
      if (filterGroups.length === 0) {
        filterGroups.push(pendingConditions);
      } else {
        filterGroups.forEach(group => {
          group.push(...pendingConditions);
        });
      }
    }

    const params: GetAdvancedParams = {
      startRow,
      endRow,
      sortProperty: this.ioCurrentSortProperty,
      sortDirection: this.ioCurrentSortDirection,
      filterGroups: filterGroups.length > 0 ? filterGroups : undefined,
      filterProperty: this.ioProjectFilterValue && this.ioProjectFilterValue > 0 ? 'ProjectId' : undefined,
      filterValue: this.ioProjectFilterValue && this.ioProjectFilterValue > 0 ? this.ioProjectFilterValue.toString() : undefined,
    };

    this.ioTableLoading$.next(true);

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.invoiceOrderService.getAdvancedInvoiceOrderForAdmin(params)
          .pipe(finalize(() => this.ioTableLoading$.next(false)))
      );

      const rows = (rsp.data?.rows || []) as InvoiceOrder[];
      const totalItems = rsp.data?.lastRow ?? rows.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.ioCurrentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.ioCurrentPage = targetPage;
        await this.getInvoiceOrders({
          page: targetPage,
          skipPageAdjustment: true,
          pendingInvoiceFilter,
          forceReload: true
        });
        return;
      }

      this.filteredInvoiceOrders$.next(rows);

      if (effectiveTotalPages > 0 && this.ioCurrentPage > effectiveTotalPages) {
        this.ioCurrentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.ioCurrentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.ioCurrentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.ioPaginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.ioCurrentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.ioDataLoaded = true;
      this.ioLastState = {
        projectFilter: this.ioProjectFilterValue,
        pendingFilter: pendingInvoiceFilter || false,
        page: this.ioCurrentPage,
        pageSize,
      };
      this.ioNeedsReload = false;
    } catch (e) {
      console.error('Error al obtener InvoiceOrders:', e);
      this.ioTableLoading$.next(false);
    }
  }

  setIOPageSize(pageSize: number, reload: boolean): void {
    const sanitizedPageSize = Math.max(1, pageSize);
    const paginationSnapshot = this.ioPaginationData$.value;

    if (paginationSnapshot.pageSize === sanitizedPageSize) {
      return;
    }

    this.ioPaginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    this.ioCurrentPage = 1;
    this.ioNeedsReload = true; // Marcar que necesita recarga

    if (reload) {
      this.getInvoiceOrders({resetPage: true, forceReload: true});
    }
  }

  setIOCurrentPage(page: number): void {
    this.ioCurrentPage = Math.max(1, page);
  }

  async updateIOSort(sortProperty: string, sortDirection: 'asc' | 'desc'): Promise<void> {
    this.ioCurrentSortProperty = sortProperty;
    this.ioCurrentSortDirection = sortDirection;
    this.ioNeedsReload = true; // Marcar que necesita recarga
    await this.getInvoiceOrders({resetPage: true, forceReload: true});
  }

  async resetIOSort(): Promise<void> {
    this.ioCurrentSortProperty = this.ioDefaultSortProperty;
    this.ioCurrentSortDirection = this.ioDefaultSortDirection;
    this.ioNeedsReload = true; // Marcar que necesita recarga
    await this.getInvoiceOrders({resetPage: true, forceReload: true});
  }

  // ========== MÉTODOS PARA PURCHASE ORDER (PO) ==========

  private buildPOFilterGroups(): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const andConditions: FilterGroup[] = [];
    const orAlternatives: FilterGroup[][][] = [];

    // Filtro de búsqueda de texto usando poSearchValue
    const searchValue = (this.poSearchValue || '').trim();
    if (searchValue) {
      const searchAlternatives = PO_SEARCH_FILTER_FIELDS.map(property => ([
        {
          property,
          value: searchValue,
          operator: 'Contains',
          operatorValue: 1
        } as FilterGroup
      ]));
      if (searchAlternatives.length) {
        orAlternatives.push(searchAlternatives);
      }
    }

    // Nota: El filtro de Proyecto se maneja con filterProperty y filterValue, no con filterGroups

    const baseAndConditions = [...baseConditions, ...andConditions];
    const filterGroups: FilterGroup[][] = [];

    if (orAlternatives.length === 0) {
      if (baseAndConditions.length === 0) {
        return [];
      }
      filterGroups.push(baseAndConditions.map(condition => this.cloneCondition(condition)));
      return filterGroups;
    }

    let combinations: FilterGroup[][] = [[]];
    orAlternatives?.forEach(alternatives => {
      const nextCombinations: FilterGroup[][] = [];
      combinations?.forEach(existing => {
        alternatives?.forEach(option => {
          nextCombinations.push([
            ...existing.map(condition => this.cloneCondition(condition)),
            ...option.map(condition => this.cloneCondition(condition)),
          ]);
        });
      });
      combinations = nextCombinations;
    });

    combinations?.forEach(combo => {
      const combined = [
        ...combo,
        ...baseAndConditions.map(condition => this.cloneCondition(condition))
      ];
      filterGroups.push(combined);
    });

    return filterGroups;
  }

  async getPurchaseOrders(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
    pendingInvoiceFilter?: boolean;
    forceReload?: boolean;
  } = {}): Promise<void> {
    const {resetPage, page, skipPageAdjustment, pendingInvoiceFilter, forceReload} = options;

    if (resetPage) {
      this.poCurrentPage = 1;
    }

    if (page !== undefined) {
      this.poCurrentPage = Math.max(1, page);
    }

    const paginationSnapshot = this.poPaginationData$.value;
    const pageSize = paginationSnapshot.pageSize && paginationSnapshot.pageSize > 0 ? paginationSnapshot.pageSize : 10;
    const currentPage = this.poCurrentPage;

    // Verificar si necesita recarga usando el estado global (usar el pageSize del estado guardado, no el calculado)
    const needsReload = this.checkPONeedsReload(pendingInvoiceFilter || false, this.poProjectFilterValue, currentPage, paginationSnapshot.pageSize || 10);

    // Si no es una recarga forzada y no necesita recarga según el estado, no hacer petición
    if (!forceReload && !needsReload && this.poDataLoaded && !resetPage && page === undefined) {
      return;
    }
    const startRow = (this.poCurrentPage - 1) * pageSize;
    const endRow = startRow + pageSize;

    const filterGroups = this.buildPOFilterGroups();

    // Agregar filtro de "Pendientes de facturar" si está activo - para PO: sin supplierInvoiceId
    if (pendingInvoiceFilter) {
      const pendingConditions: FilterGroup[] = [
        {
          property: 'SupplierInvoiceId',
          value: null,
          operator: 'Equals'
        }
      ];
      if (filterGroups.length === 0) {
        filterGroups.push(pendingConditions);
      } else {
        filterGroups.forEach(group => {
          group.push(...pendingConditions);
        });
      }
    }

    const params: GetAdvancedParams = {
      startRow,
      endRow,
      sortProperty: this.poCurrentSortProperty,
      sortDirection: this.poCurrentSortDirection,
      filterGroups: filterGroups.length > 0 ? filterGroups : undefined,
      filterProperty: this.poProjectFilterValue && this.poProjectFilterValue > 0 ? 'ProjectId' : undefined,
      filterValue: this.poProjectFilterValue && this.poProjectFilterValue > 0 ? this.poProjectFilterValue.toString() : undefined,
    };

    this.poTableLoading$.next(true);

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.invoiceOrderService.getAdvancedPurchaseOrderForAdmin(params)
          .pipe(finalize(() => this.poTableLoading$.next(false)))
      );

      const rows = (rsp.data?.rows || []) as PurchaseOrder[];
      const totalItems = rsp.data?.lastRow ?? rows.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.poCurrentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.poCurrentPage = targetPage;
        await this.getPurchaseOrders({
          page: targetPage,
          skipPageAdjustment: true,
          pendingInvoiceFilter,
          forceReload: true
        });
        return;
      }

      this.filteredPurchaseOrders$.next(rows);

      if (effectiveTotalPages > 0 && this.poCurrentPage > effectiveTotalPages) {
        this.poCurrentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.poCurrentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.poCurrentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.poPaginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.poCurrentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.poDataLoaded = true;
      // Actualizar estado guardado después de cargar
      this.poLastState = {
        projectFilter: this.poProjectFilterValue,
        pendingFilter: pendingInvoiceFilter || false,
        page: this.poCurrentPage,
        pageSize: pageSize
      };
      this.poNeedsReload = false;
    } catch (e) {
      console.error('Error al obtener PurchaseOrders:', e);
      this.poTableLoading$.next(false);
    }
  }

  setPOPageSize(pageSize: number, reload: boolean): void {
    const sanitizedPageSize = Math.max(1, pageSize);
    const paginationSnapshot = this.poPaginationData$.value;

    if (paginationSnapshot.pageSize === sanitizedPageSize) {
      return;
    }

    this.poPaginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    this.poCurrentPage = 1;
    this.poNeedsReload = true; // Marcar que necesita recarga

    if (reload) {
      this.getPurchaseOrders({resetPage: true, forceReload: true});
    }
  }

  setPOCurrentPage(page: number): void {
    this.poCurrentPage = Math.max(1, page);
  }

  async updatePOSort(sortProperty: string, sortDirection: 'asc' | 'desc'): Promise<void> {
    this.poCurrentSortProperty = sortProperty;
    this.poCurrentSortDirection = sortDirection;
    this.poNeedsReload = true; // Marcar que necesita recarga
    await this.getPurchaseOrders({resetPage: true, forceReload: true});
  }

  async resetPOSort(): Promise<void> {
    this.poCurrentSortProperty = this.poDefaultSortProperty;
    this.poCurrentSortDirection = this.poDefaultSortDirection;
    this.poNeedsReload = true; // Marcar que necesita recarga
    await this.getPurchaseOrders({resetPage: true, forceReload: true});
  }

  // ========== MÉTODOS PARA ANALYTIC OPERATION (AO) ==========

  // Claves estables para comparar el estado de la pestaña entre cargas: sin ellas, pulsar un chip
  // nuevo no dispara recarga porque checkAONeedsReload no ve el cambio.
  private get aoClassFilterKey(): string {
    return [...(this.aoClassFilterValues || [])].sort().join('|');
  }

  private get aoContainerRefsKey(): string {
    return [...(this.aoAdvancedContainerRefs || [])].sort().join('|');
  }

  get aoHasAdvancedContainerFilter(): boolean {
    return (this.aoAdvancedContainerRefs || []).length > 0;
  }

  get aoClassFilterActive(): boolean {
    const selected = this.aoClassFilterValues || [];
    return selected.length > 0 && selected.length < AO_CLASS_VALUES.length;
  }

  get aoClassFilterLabel(): string {
    const selected = this.aoClassFilterValues || [];
    if (selected.length === 0 || selected.length === AO_CLASS_VALUES.length) {
      return 'Clase';
    }
    return AO_CLASS_VALUES.filter(value => selected.includes(value))
      .map(value => AO_CLASS_LABELS[value])
      .join(', ');
  }

  isAOClassSelected(value: string): boolean {
    return (this.aoClassFilterValues || []).includes(value);
  }

  toggleAOClassFilter(value: string): void {
    const selected = this.aoClassFilterValues || [];
    this.aoClassFilterValues = selected.includes(value)
      ? selected.filter(item => item !== value)
      : [...selected, value];
  }

  private buildAOFilterGroups(): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const orAlternatives: FilterGroup[][][] = [];

    const searchValue = (this.aoSearchValue || '').trim();
    if (searchValue) {
      const searchAlternatives = AO_SEARCH_FILTER_FIELDS.map(property => ([
        {
          property,
          value: searchValue,
          operator: 'Contains',
          operatorValue: 1
        } as FilterGroup
      ]));
      if (searchAlternatives.length) {
        orAlternatives.push(searchAlternatives);
      }
    }

    // El filtro de contenedores comparte semántica con el de la pestaña "Ver órdenes", pero aquí el
    // camino no cruza ninguna colección: la OA navega directa a su orden de servicio. Se usa Equals
    // (0) y no Contains, para que "MSCU1234" no arrastre "MSCU12345". Ver SOU-497.
    const containerRefs = this.aoAdvancedContainerRefs || [];
    if (containerRefs.length > 0) {
      orAlternatives.push(
        containerRefs.map(containerRef => ([
          this.createEqualsCondition('ServiceOrder.ProjectContainer.ContainerRef', containerRef, undefined, 0)
        ]))
      );
    }

    if (this.aoTypeFilterValue) {
      baseConditions.push({
        property: 'Type',
        value: this.aoTypeFilterValue,
        // operatorValue 1 = Contains: se declara explícito porque es lo que el backend aplica de
        // verdad (operator y operatorValue escriben el mismo campo allí, y gana el numérico).
        operator: 'Contains',
        operatorValue: 1
      });
    }

    // Una sola condición para la clase, en vez de una alternativa OR por cada valor: así no se
    // multiplica el producto cartesiano de más abajo. Con las tres marcadas no se filtra nada.
    const selectedClasses = this.aoClassFilterValues || [];
    if (selectedClasses.length > 0 && selectedClasses.length < AO_CLASS_VALUES.length) {
      if (selectedClasses.length === AO_CLASS_VALUES.length - 1) {
        const excludedClass = AO_CLASS_VALUES.find(value => !selectedClasses.includes(value));
        baseConditions.push({
          property: 'Class',
          value: excludedClass as string,
          operator: 'NotEquals',
          operatorValue: 9
        });
      } else {
        baseConditions.push({
          property: 'Class',
          value: selectedClasses[0],
          operator: 'Equals',
          operatorValue: 0
        });
      }
    }

    // En esta pestaña InvoiceOrderId y PurchaseOrderId son propiedades directas de la entidad, así
    // que la condición es más simple que en "Ver órdenes": sin navegación ni collectionGroup.
    if (this.aoWithoutInvoice) {
      baseConditions.push(this.createEqualsCondition('InvoiceOrderId', null, undefined, 0));
    }

    if (this.aoWithoutPurchaseOrder) {
      baseConditions.push(this.createEqualsCondition('PurchaseOrderId', null, undefined, 0));
    }

    if (orAlternatives.length === 0) {
      if (baseConditions.length === 0) {
        return [];
      }
      return [baseConditions.map(condition => this.cloneCondition(condition))];
    }

    let combinations: FilterGroup[][] = [[]];
    orAlternatives.forEach(alternatives => {
      const nextCombinations: FilterGroup[][] = [];
      combinations.forEach(existing => {
        alternatives.forEach(option => {
          nextCombinations.push([
            ...existing.map(condition => this.cloneCondition(condition)),
            ...option.map(condition => this.cloneCondition(condition)),
          ]);
        });
      });
      combinations = nextCombinations;
    });

    return combinations.map(combo => [
      ...combo,
      ...baseConditions.map(condition => this.cloneCondition(condition))
    ]);
  }

  private checkAONeedsReload(page: number, pageSize: number): boolean {
    const currentState = {
      typeFilter: this.aoTypeFilterValue,
      classFilter: this.aoClassFilterKey,
      withoutInvoice: this.aoWithoutInvoice,
      withoutPurchaseOrder: this.aoWithoutPurchaseOrder,
      containerRefs: this.aoContainerRefsKey,
      searchValue: (this.aoSearchValue || '').trim(),
      page,
      pageSize
    };

    if (!this.aoLastState) {
      return true;
    }

    const needsReload =
      this.aoLastState.typeFilter !== currentState.typeFilter ||
      this.aoLastState.classFilter !== currentState.classFilter ||
      this.aoLastState.withoutInvoice !== currentState.withoutInvoice ||
      this.aoLastState.withoutPurchaseOrder !== currentState.withoutPurchaseOrder ||
      this.aoLastState.containerRefs !== currentState.containerRefs ||
      this.aoLastState.searchValue !== currentState.searchValue ||
      this.aoLastState.page !== currentState.page ||
      this.aoLastState.pageSize !== currentState.pageSize;

    return needsReload || this.aoNeedsReload;
  }

  async getAnalyticOperations(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
    forceReload?: boolean;
  } = {}): Promise<void> {
    const {resetPage, page, skipPageAdjustment, forceReload} = options;

    if (resetPage) {
      this.aoCurrentPage = 1;
    }

    if (page !== undefined) {
      this.aoCurrentPage = Math.max(1, page);
    }

    const paginationSnapshot = this.aoPaginationData$.value;
    const pageSize = paginationSnapshot.pageSize && paginationSnapshot.pageSize > 0 ? paginationSnapshot.pageSize : 10;
    const currentPage = this.aoCurrentPage;
    const needsReload = this.checkAONeedsReload(currentPage, paginationSnapshot.pageSize || 10);

    if (!forceReload && !needsReload && this.aoDataLoaded && !resetPage && page === undefined) {
      return;
    }

    const startRow = (this.aoCurrentPage - 1) * pageSize;
    const endRow = startRow + pageSize;
    const filterGroups = this.buildAOFilterGroups();

    const params: GetAdvancedParams = {
      startRow,
      endRow,
      sortProperty: this.aoCurrentSortProperty,
      sortDirection: this.aoCurrentSortDirection,
      filterGroups: filterGroups.length > 0 ? filterGroups : undefined,
    };

    this.aoTableLoading$.next(true);

    try {
      const rsp: GetResponse = await firstValueFrom(
        this.analyticOperationService.getAdvancedAnalyticOperationForAdmin(params)
          .pipe(finalize(() => this.aoTableLoading$.next(false)))
      );

      const rows = ((rsp.data?.rows || []) as AnalyticOperation[])
        .map(ao => ({
          ...ao,
          serviceOrderId: ao.serviceOrderId ?? ao.serviceOrder?.id,
          projectId: ao.projectId ?? ao.project?.id,
          invoiceOrderId: ao.invoiceOrderId ?? ao.invoiceOrder?.id ?? null,
          purchaseOrderId: ao.purchaseOrderId ?? ao.purchaseOrder?.id ?? null,
          currency: ao.currencyId ? this.gs.currencies$.value?.find(c => c.id === ao.currencyId) : ao.currency
        } as AnalyticOperation));
      const totalItems = rsp.data?.lastRow ?? rows.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.aoCurrentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.aoCurrentPage = targetPage;
        await this.getAnalyticOperations({
          page: targetPage,
          skipPageAdjustment: true,
          forceReload: true
        });
        return;
      }

      this.filteredAnalyticOperations$.next(rows);

      if (effectiveTotalPages > 0 && this.aoCurrentPage > effectiveTotalPages) {
        this.aoCurrentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.aoCurrentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.aoCurrentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.aoPaginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.aoCurrentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.aoDataLoaded = true;
      this.aoLastState = {
        typeFilter: this.aoTypeFilterValue,
        classFilter: this.aoClassFilterKey,
        withoutInvoice: this.aoWithoutInvoice,
        withoutPurchaseOrder: this.aoWithoutPurchaseOrder,
        containerRefs: this.aoContainerRefsKey,
        searchValue: (this.aoSearchValue || '').trim(),
        page: this.aoCurrentPage,
        pageSize,
      };
      this.aoNeedsReload = false;
    } catch (e) {
      console.error('Error al obtener AnalyticOperations:', e);
      this.aoTableLoading$.next(false);
    }
  }

  setAOPageSize(pageSize: number, reload: boolean): void {
    const sanitizedPageSize = Math.max(1, pageSize);
    const paginationSnapshot = this.aoPaginationData$.value;

    if (paginationSnapshot.pageSize === sanitizedPageSize) {
      return;
    }

    this.aoPaginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    this.aoCurrentPage = 1;
    this.aoNeedsReload = true;

    if (reload) {
      this.getAnalyticOperations({resetPage: true, forceReload: true});
    }
  }

  setAOCurrentPage(page: number): void {
    this.aoCurrentPage = Math.max(1, page);
  }

  async updateAOSort(sortProperty: string, sortDirection: 'asc' | 'desc'): Promise<void> {
    this.aoCurrentSortProperty = sortProperty;
    this.aoCurrentSortDirection = sortDirection;
    this.aoNeedsReload = true;
    await this.getAnalyticOperations({resetPage: true, forceReload: true});
  }

  async resetAOSort(): Promise<void> {
    this.aoCurrentSortProperty = this.aoDefaultSortProperty;
    this.aoCurrentSortDirection = this.aoDefaultSortDirection;
    this.aoNeedsReload = true;
    await this.getAnalyticOperations({resetPage: true, forceReload: true});
  }
}

